import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GripVertical,
  Plus,
  Edit2,
  Trash2,
  Loader2,
} from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { taskStatusConfigsApi } from '@/lib/api';
import { StatusModal } from '@/components/modals/StatusModal';
import { DeleteStatusModal } from '@/components/modals/DeleteStatusModal';

interface TaskStatus {
  id: string;
  name: string;
  slug: string;
  color: string;
  position: number;
  isDefault: boolean;
  isClosed: boolean;
}

function SortableStatusRow({ status, onEdit, onDelete }: {
  status: TaskStatus;
  onEdit: (status: TaskStatus) => void;
  onDelete: (status: TaskStatus) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-4 py-4 px-4 bg-white rounded-lg border transition-all',
        isDragging ? 'opacity-50 shadow-lg' : 'border-grey-100'
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-grey-400 hover:text-grey-600 cursor-grab active:cursor-grabbing"
        title="Drag to reorder"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      {/* Color swatch */}
      <div
        className="w-4 h-4 rounded flex-shrink-0"
        style={{ backgroundColor: status.color }}
      />

      {/* Status name */}
      <div className="flex-1">
        <p className="font-medium text-secondary">{status.name}</p>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2">
        {status.isDefault && (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent/10 text-accent">
            Default
          </span>
        )}
        {status.isClosed && (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success/10 text-success">
            Closed
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(status)}
          className="p-2 text-grey-600 hover:text-secondary hover:bg-grey-100 rounded transition-colors"
          title="Edit status"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(status)}
          className="p-2 text-grey-600 hover:text-error hover:bg-error/10 rounded transition-colors"
          title="Delete status"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function SettingsStatuses() {
  const queryClient = useQueryClient();
  const [editingStatus, setEditingStatus] = useState<TaskStatus | null>(null);
  const [deletingStatus, setDeletingStatus] = useState<TaskStatus | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { distance: 8 }),
    useSensor(KeyboardSensor)
  );

  // Fetch task status configs
  const { data: statuses, isLoading } = useQuery({
    queryKey: ['taskStatusConfigs'],
    queryFn: () => taskStatusConfigsApi.list(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; color: string; isDefault?: boolean; isClosed?: boolean }) =>
      taskStatusConfigsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskStatusConfigs'] });
      setShowAddModal(false);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: {
      id: string;
      data: { name?: string; color?: string; isDefault?: boolean; isClosed?: boolean };
    }) => taskStatusConfigsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskStatusConfigs'] });
      setEditingStatus(null);
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => taskStatusConfigsApi.reorder(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskStatusConfigs'] });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !statuses) return;

    const oldIndex = statuses.findIndex(s => s.id === active.id);
    const newIndex = statuses.findIndex(s => s.id === over.id);

    if (oldIndex === newIndex) return;

    // arrayMove gives correct slide-to-position behavior (swap only exchanges two elements).
    const newOrder = arrayMove(statuses, oldIndex, newIndex);

    // Update .position fields so KanbanBoard's position-based sort sees the correct order from cache.
    const withUpdatedPositions = newOrder.map((s, index) => ({ ...s, position: index }));

    // Optimistically update the shared cache.
    queryClient.setQueryData(['taskStatusConfigs'], withUpdatedPositions);

    // Call API to persist
    reorderMutation.mutate(newOrder.map(s => s.id));
  };

  const handleAddStatus = (data: { name: string; color: string; isDefault?: boolean; isClosed?: boolean }) => {
    createMutation.mutate(data);
  };

  const handleUpdateStatus = (data: { name: string; color: string; isDefault?: boolean; isClosed?: boolean }) => {
    if (editingStatus) {
      updateMutation.mutate({ id: editingStatus.id, data });
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-heading-3 text-secondary">Task Statuses</h2>
            <p className="text-sm text-grey-500 mt-1">
              Define the workflow statuses available for tasks across all projects. Statuses determine the columns in the board view.
            </p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            disabled={createMutation.isPending}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Status
          </Button>
        </div>

        {statuses && statuses.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={statuses.map(s => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {statuses.map((status) => (
                  <SortableStatusRow
                    key={status.id}
                    status={status}
                    onEdit={(s) => setEditingStatus(s)}
                    onDelete={(s) => setDeletingStatus(s)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="py-8 text-center text-grey-500">
            No statuses found
          </div>
        )}
      </Card>

      {/* Add/Edit Status Modal */}
      <StatusModal
        isOpen={showAddModal || !!editingStatus}
        onClose={() => {
          setShowAddModal(false);
          setEditingStatus(null);
        }}
        onSubmit={editingStatus ? handleUpdateStatus : handleAddStatus}
        status={editingStatus || undefined}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Status Modal */}
      {deletingStatus && (
        <DeleteStatusModal
          status={deletingStatus}
          onClose={() => setDeletingStatus(null)}
          statuses={statuses || []}
        />
      )}
    </div>
  );
}
